import { computed } from "vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { ActivityLogFilters } from "./activityLogTypes";

const keyList = (value?: string): string[] | undefined => {
  const keys = [...new Set((value ?? "").split(",").filter(Boolean))];
  return keys.length ? keys : undefined;
};

/** Keeps the activity log filters in the URL, so a filtered view is a link and survives browser history. */
export function useActivityLogQuery() {
  const { getQueryValue, updateQuery, parsers } = useQueryManager();
  const { globalErrorHandler } = useGlobalHandlers();
  const accountId = getQueryValue("account_id", parsers.positiveId);
  const companyId = getQueryValue("company_id", parsers.positiveId);
  const orderId = getQueryValue("order_id", parsers.positiveId);
  const portfolioId = getQueryValue("portfolio_id", parsers.positiveId);
  const salesTransactionId = getQueryValue(
    "sales_transaction_id",
    parsers.positiveId
  );
  const activityKeys = getQueryValue("activity_keys", parsers.string);
  const createdFrom = getQueryValue("created_from", parsers.isoInstant);
  const createdTo = getQueryValue("created_to", parsers.isoInstant);

  const filters = computed<ActivityLogFilters>({
    get: () => ({
      account_id: accountId.value,
      company_id: companyId.value,
      order_id: orderId.value,
      portfolio_id: portfolioId.value,
      sales_transaction_id: salesTransactionId.value,
      activity_keys: keyList(activityKeys.value),
      created_from: createdFrom.value,
      created_to: createdTo.value,
    }),
    // Every key is written, so a filter missing from `value` is cleared from the URL.
    set: (value) => {
      updateQuery({
        account_id: value.account_id?.toString(),
        company_id: value.company_id?.toString(),
        order_id: value.order_id?.toString(),
        portfolio_id: value.portfolio_id?.toString(),
        sales_transaction_id: value.sales_transaction_id?.toString(),
        activity_keys: value.activity_keys?.join(",") || undefined,
        created_from: value.created_from,
        created_to: value.created_to,
      }).catch(globalErrorHandler);
    },
  });

  return { filters };
}
